import clsx from 'clsx'
import { type ReactNode, useCallback, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'

/**
 * Selectors for "focusable" elements — same set jQuery UI / Radix use. We
 * exclude `[tabindex="-1"]` so the dialog wrapper itself doesn't show up in
 * the trap loop (its tabindex=-1 lets us programmatically focus it without
 * making it Tab-stoppable).
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

function getFocusable(root: HTMLElement): HTMLElement[] {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
  return nodes.filter((n) => !n.hasAttribute('aria-hidden') && n.offsetParent !== null)
}

export type ModalSize = 'sm' | 'md' | 'lg'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  size?: ModalSize
  closeOnBackdrop?: boolean
  /** Optional footer slot (typically Buttons). */
  footer?: ReactNode
  /** Optional explicit aria-describedby target id (otherwise the body is described). */
  describedById?: string
}

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
}

export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
  closeOnBackdrop = true,
  footer,
  describedById,
}: ModalProps) {
  const labelId = useId()
  const bodyId = useId()
  const dialogRef = useRef<HTMLDivElement | null>(null)

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      // Focus trap: cycle Tab / Shift+Tab inside the dialog so keyboard users
      // can't tab into the underlying page (which is `aria-hidden` to AT but
      // still receives DOM focus without a trap). Per WAI-ARIA APG dialog
      // pattern.
      if (e.key !== 'Tab') return
      const dialog = dialogRef.current
      if (!dialog) return
      const focusable = getFocusable(dialog)
      if (focusable.length === 0) {
        // Nothing tabbable inside — keep focus on the dialog itself.
        e.preventDefault()
        dialog.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault()
          last?.focus()
        }
      } else {
        if (active === last || !dialog.contains(active)) {
          e.preventDefault()
          first?.focus()
        }
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (!open) return
    document.addEventListener('keydown', handleKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // Remember what had focus before opening so we can restore it on close.
    const previouslyFocused = document.activeElement as HTMLElement | null
    // Move focus into the dialog (the wrapper is tabIndex=-1).
    dialogRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = prevOverflow
      // Restore focus to the trigger so screen-reader / keyboard users land
      // back where they were before the modal opened.
      previouslyFocused?.focus?.()
    }
  }, [open, handleKey])

  if (!open) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="presentation">
      <button
        type="button"
        aria-label="Close dialog"
        tabIndex={-1}
        onClick={() => closeOnBackdrop && onClose()}
        className="absolute inset-0 bg-black/30 backdrop-blur-md cursor-default"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        aria-describedby={describedById ?? bodyId}
        tabIndex={-1}
        className={clsx(
          // Apple-style modal: white surface, 20px radius, soft elevation.
          'relative w-full rounded-[var(--radius-xl)] border border-[var(--border)]',
          'bg-[var(--bg)] text-[var(--text)] outline-none',
          'shadow-[0_24px_72px_-16px_rgba(0,0,0,0.25)]',
          sizeClass[size],
        )}
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <h2 id={labelId} className="text-[15px] font-semibold">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[var(--text-2)] hover:text-[var(--text)] focus-visible:shadow-[var(--focus-ring)] rounded-[var(--radius-sm)] outline-none px-1"
          >
            ×
          </button>
        </div>
        <div id={bodyId} className="p-4">
          {children}
        </div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] px-4 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
